import React from 'react';
import { motion } from 'framer-motion';
import ScrollAnimation from './ScrollAnimation';
import cs50xImage from '../assets/cs50x.jpg';
import cs50pImage from '../assets/cs50p.jpg';
import cs50wImage from '../assets/cs50w.jpg';

const certificates = [
  {
    title: 'CS50x: Introduction to Computer Science',
    issuer: 'Harvard University',
    year: '2023',
    image: cs50xImage,
    href: 'https://courses.edx.org/certificates/c82f1ca49d8e4ca78d19f261288a2ae2',
    description: 'Comprehensive introduction to computer science and programming.',
    badge: '🏆',
  },
  {
    title: 'CS50P: Introduction to Programming with Python',
    issuer: 'Harvard University',
    year: '2023',
    image: cs50pImage,
    href: 'https://courses.edx.org/certificates/f51a72edad7648f68f042f90fb16e44a',
    description: 'Deep dive into Python programming and problem solving.',
    badge: '🐍',
  },
  {
    title: 'CS50W: Web Programming with Python and JavaScript',
    issuer: 'Harvard University',
    year: '2023',
    image: cs50wImage,
    href: '#',
    description: 'Full-stack web development with Django and React.',
    badge: '🌐',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 50 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15,
    },
  },
};

const Certificates = () => (
  <ScrollAnimation>
    <motion.section
      id="certificates"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
    >
      <motion.h2
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        Certificates
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{ marginBottom: '2rem' }}
      >
        Verified credentials from world-class institutions, showcasing my commitment to continuous learning.
      </motion.p>

      <motion.div
        className="grid-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {certificates.map((cert, index) => (
          <motion.div
            key={cert.title}
            className="card certificate-card"
            variants={cardVariants}
            whileHover={{
              y: -12,
              scale: 1.02,
              transition: { type: 'spring', stiffness: 400, damping: 25 },
            }}
            style={{ position: 'relative' }}
          >
            {/* Badge */}
            <motion.div
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                fontSize: '1.5rem',
                zIndex: 10,
                background: 'rgba(10, 10, 15, 0.8)',
                backdropFilter: 'blur(10px)',
                padding: '0.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-glass)',
              }}
              whileHover={{ scale: 1.2, rotate: 10 }}
            >
              {cert.badge}
            </motion.div>

            {/* Image with overlay */}
            <motion.div
              style={{
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <motion.img
                src={cert.image}
                alt={cert.title}
                className="card-image"
                style={{ filter: 'brightness(0.9)' }}
                whileHover={{ scale: 1.08, filter: 'brightness(1)' }}
                transition={{ duration: 0.4 }}
              />
              
              {/* Gradient overlay */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, transparent 30%, rgba(10, 10, 15, 0.95) 100%)',
                  pointerEvents: 'none',
                }}
              />

              {/* Animated shine effect */}
              <motion.div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                }}
                animate={{
                  transform: ['translateX(-100%)', 'translateX(200%)'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 2,
                  ease: 'easeInOut',
                }}
              />
            </motion.div>

            <div className="card-content">
              <motion.h3
                style={{
                  fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                }}
              >
                {cert.title}
              </motion.h3>

              <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                {cert.description}
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 'clamp(0.65rem, 2vw, 0.8rem)',
                    padding: '0.2rem 0.5rem',
                    background: 'rgba(124, 58, 237, 0.15)',
                    border: '1px solid rgba(124, 58, 237, 0.3)',
                    borderRadius: '100px',
                    color: 'var(--aurora-2)',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  📍 {cert.issuer}
                </span>
                <span
                  style={{
                    fontSize: 'clamp(0.65rem, 2vw, 0.8rem)',
                    padding: '0.2rem 0.5rem',
                    background: 'rgba(52, 211, 153, 0.15)',
                    border: '1px solid rgba(52, 211, 153, 0.3)',
                    borderRadius: '100px',
                    color: 'var(--aurora-4)',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  📅 {cert.year}
                </span>
              </div>

              <motion.a
                href={cert.href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{
                  scale: 1.05,
                  x: 5,
                }}
                whileTap={{ scale: 0.95 }}
              >
                <span>View Certificate</span>
              </motion.a>
            </div>

            {/* Animated border glow */}
            <motion.div
              style={{
                position: 'absolute',
                inset: -1,
                background: `conic-gradient(from ${index * 120}deg, var(--aurora-1), var(--aurora-2), var(--aurora-3), var(--aurora-1))`,
                borderRadius: '21px',
                zIndex: -1,
                opacity: 0,
              }}
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
              }}
              whileHover={{ opacity: 0.4 }}
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  </ScrollAnimation>
);

export default Certificates;
